import {
  type ClientRect,
  type CollisionDescriptor,
  type CollisionDetection,
  type DragOverEvent,
  type Modifier,
} from '@dnd-kit/core';

import Animations from '@dynatrace/strato-design-tokens/animations';

import { dataTableV2RowDraggingCSS } from './RowOrder.sty.js';
import type { DragAndDropTransform, Row } from '../../hooks/useTable/types.js';
import { ROW_EXPANDED_ID_SUFFIX } from '../RowDetails/RowDetails.js';

// #region CellTransforms
// all rows that were transformed in the last onDragOver call
let lastAffectedRows = new Set<string>();

/**
 * Transforms cells upwards or downwards to make space for the row which is currently being dragged ("active" row).
 * @param table - the table where the row ordering is happening.
 * @param event - contains "active" (row being dragged) and "over" ids (row where the active row is currently dragged over).
 * @param rowOrder - original row order before dragging.
 * @param rowSeparationNone - whether the rows are visually separated by a border.
 * @internal
 */
export function transformCellsOnDragOver(
  table: HTMLElement,
  event: DragOverEvent,
  rowOrder: string[] | undefined,
  rowSeparationNone: boolean,
): void {
  let rowHeight = event.active?.rect.current.translated?.height;
  const activeRow = event.active.data.current?.row as Row<unknown>;
  if (
    rowOrder === undefined ||
    event.collisions === null ||
    event.active === null ||
    event.over === null ||
    activeRow === undefined ||
    rowHeight === undefined
  ) {
    return;
  }

  // visually disable rows that do not share the same parent as the dragged row
  styleNonTargetRows(table, activeRow.parentId);

  // keep track of all rows that are transformed during the current onDragOver call
  const newAffectedRows = new Set<string>();
  const { fromIndex, toIndex } = getDragIndexes(
    rowOrder,
    event.active.id as string, // for us the IDs are always strings
    event.over.id as string, // for us the IDs are always strings
  );

  const activeRowIsLastRow = fromIndex === rowOrder.length - 1;

  // if rowSeparation is active, consider 1px border for all rows
  // except if dragging the last row or if row details are open
  if (
    !rowSeparationNone &&
    !activeRowIsLastRow &&
    !activeRow.getIsDetailsOpen()
  ) {
    rowHeight += 1;
  }

  if (fromIndex < toIndex) {
    transformRows(
      table,
      rowOrder.slice(fromIndex + 1, toIndex + 1),
      -rowHeight,
      newAffectedRows,
    );
  } else if (fromIndex > toIndex) {
    transformRows(
      table,
      rowOrder.slice(toIndex, fromIndex),
      rowHeight,
      newAffectedRows,
    );
  }

  // Reset rows that were translated in the last drag operation but are not in the current one
  transformRows(table, lastAffectedRows, 0);
  lastAffectedRows = newAffectedRows;
}

/**
 * Moves rows on the y-axis by the given number of pixels.
 * @param table - the table grid element.
 * @param rowIds - the ids of rows to transform.
 * @param amountToTransform - number of pixels to translate (positive for down or negative for up)
 * @param newAffectedRows - set to save all rows that are transformed during the current onDragOver call
 */
function transformRows(
  table: HTMLElement,
  rowIds: string[] | Set<string>,
  amountToTransform: number,
  newAffectedRows?: Set<string>,
): void {
  for (const rowId of rowIds) {
    const row = getRowWithId(table, rowId);
    if (row.length > 0) {
      translateCellsY(row, amountToTransform);
      if (newAffectedRows) {
        lastAffectedRows.delete(rowId);
        newAffectedRows.add(rowId);
      }
    }
  }
}

/**
 * Applies styles to visually indicate droppable targets for the currently active row.
 * Rows that do not share the same parent as the dragged row will appear visually disabled.
 *
 * @param table - The table element containing the rows to style.
 * @param activeParentId - parent id of the currently dragged row.
 */
function styleNonTargetRows(table: HTMLElement, activeParentId?: string) {
  // if the dragged row is a top-level row, we don't want to add any styles
  if (activeParentId === undefined) {
    return;
  }
  const rows = getAllNonTargetRowsInTableBody(table, activeParentId);
  for (const row of rows) {
    row.classList.add(dataTableV2RowDraggingCSS.disabled);
  }
}

/**
 * Moves cells on the y-axis by the given number of pixels. Applies a transition
 * also to make it looks smoother, using strato tokens for duration and easing.
 * @param cells - cells to translate
 * @param value - number of pixels (positive for down or negative for up)
 */
function translateCellsY(cells: HTMLElement[], value: number): void {
  const transform = `translateY(${value}px)`;
  const transition = `transform ${Animations.SlideIn.Duration.Base} ${Animations.Move.Easing}`;
  for (const cell of cells) {
    cell.style.transform = transform;
    cell.style.transition = transition;
  }
}

/**
 * Resets the transform style on the given cells.
 * @param cells - the cells we want to reset.
 * @internal
 */
export function resetAllStylesForCells(cells: HTMLElement[]): void {
  if (cells.length === 0) {
    return;
  }
  for (const cell of cells) {
    cell.style.removeProperty('transform');
    cell.style.removeProperty('transition');
    cell.classList.remove(dataTableV2RowDraggingCSS.disabled);
  }
}

/**
 * Gets the row elements with a given row id (including row details row).
 * @param table - the current table.
 * @param id - id of the row (matches against `data-rowid`).
 * @returns matching rows.
 * @internal
 */
export function getRowWithId(
  table: HTMLElement,
  id: string | number | undefined,
): HTMLElement[] {
  if (id === undefined) {
    return [];
  }
  const allCellsInRow = table.querySelectorAll<HTMLElement>(
    `[data-rowid="${CSS.escape(String(id))}"], [data-rowid="${CSS.escape(
      String(id),
    )}${ROW_EXPANDED_ID_SUFFIX}"]`,
  );

  return Array.from(allCellsInRow);
}

/**
 * Gets all rows in the given table that are not within the same sub-row parent.
 *
 * @param table - the table we are concerned with.
 * @param parentIdToExclude - parent id to be excluded
 * @returns all cells in the table (div elements with role="cell"/role="presentation" and a data-rowid attribute).
 * @internal
 */
export function getAllNonTargetRowsInTableBody(
  table: HTMLElement,
  parentIdToExclude?: string,
): HTMLElement[] {
  const nonTargetRows = table.querySelectorAll<HTMLElement>(
    `[data-rowgroup="body"] [role="row"]:not([data-rowid*="${CSS.escape(
      String(parentIdToExclude),
    )}"]`,
  );

  return Array.from(nonTargetRows);
}

/**
 * Gets all cells and rows in the given table (all cells that have role="cell"/role="presentation" or rows with the "data-row" attribute).
 * @param table - the table we are concerned with.
 * @returns all cells and rows in the table.
 * @internal
 */
export function getAllRowsInTableBody(table: HTMLElement): HTMLElement[] {
  const allCellsInTableBody = table.querySelectorAll<HTMLElement>(
    '[role="row"][data-rowid]',
  );

  return Array.from(allCellsInTableBody);
}

/**
 * @param table - the table we are concerned with.
 * @param rowNumber - zero-based row number
 * @returns the drag handle of the given row for drag and drop ordering
 * @internal
 */
export function getDragHandleForRow(
  table: HTMLElement,
  rowNumber: number,
): HTMLElement | null {
  return table.querySelector<HTMLElement>(
    `[role="cell"][data-index='row-${rowNumber}-column-0'] button`,
  );
}
// #region CellTransforms

/**
 * Gets the `fromIndex` and `toIndex` from the over and active id.
 * @param rowOrder - contains the ids of the rows in order.
 * @param activeId - is the id of the currently actively dragged row
 * @param overId - is the target id of where the dragging row is currently dragged to
 * @returns indexes from where a row is dragged and where it is dragged to
 * @internal
 */
export function getDragIndexes(
  rowOrder: string[],
  activeId: string,
  overId: string,
): { fromIndex: number; toIndex: number } {
  const fromIndex = rowOrder.findIndex((id) => id === activeId);
  const toIndex = rowOrder.findIndex((id) => id === overId);
  return { fromIndex, toIndex };
}

// #region CollisionDetection
/**
 * Check if a given point is contained within a bounding rectangle
 */
function isPointWithinRectY(pointY: number, rect: ClientRect): boolean {
  return rect.top <= pointY && pointY <= rect.bottom;
}

/**
 * Custom collision detection algorithm for row order drag and drop.
 * Determines the closest droppable target within the Y-axis based on the dragging direction.
 *
 * @param param0 - Object containing the droppable containers, the droppable rects, the collision rect and
 *                 the active element, which are needed for the collision detection.
 * @returns An array of collision descriptors, sorted by the distance to the dragged row, or an empty array if none is found.
 * @internal
 */
export const closestTargetWithinY: CollisionDetection = ({
  droppableContainers,
  droppableRects,
  collisionRect,
  active,
}) => {
  const initialRect = active.rect.current.initial;

  if (initialRect === null) {
    return [];
  }

  const collisions: CollisionDescriptor[] = [];

  // To make this work for varying row heights, depending on the dragging direction
  // we take either the top or the bottom of the dragged row to check for collisions
  // (minus a small offset, so the rows have to overlap a bit, before they would be swapped)
  const isDraggingUp = collisionRect.top < initialRect.top;
  // min row height is 24px, to ensure the offset is in the top/bottom half: 24 / 2 - 1 = 11
  const COLLISION_OFFSET = 11;
  const collisionRectY = isDraggingUp
    ? collisionRect.top + COLLISION_OFFSET
    : collisionRect.bottom - COLLISION_OFFSET;

  for (const droppableContainer of droppableContainers) {
    const { id, node } = droppableContainer;
    const rect = droppableRects.get(id);
    if (rect === undefined) {
      continue;
    }

    // the droppable containers do not consider the row details height, so we need to get that
    // this can be removed once we have subgrids and can put the ref on the row itself (APPDEV-12251)
    const heightAdaptedRect = { ...rect };
    const rowDetailsElement = node.current?.parentElement?.querySelector(
      `[data-rowid="${CSS.escape(String(id))}${ROW_EXPANDED_ID_SUFFIX}"]`,
    );

    if (rowDetailsElement) {
      const detailsHeight = rowDetailsElement.getBoundingClientRect().height;
      heightAdaptedRect.height += detailsHeight;
      heightAdaptedRect.bottom += detailsHeight;
    }

    if (isPointWithinRectY(collisionRectY, heightAdaptedRect)) {
      // return the container that the active rect is dragged over
      return [
        {
          id,
          data: { droppableContainer },
        },
      ];
    }

    // in case the dragged row's collisionRectY is not directly within any of the droppable containers
    // (e.g. when dragging over the scrollbar) we need to find the closest one. Therefore, we calculate
    // the distance between the droppable container's top/bottom and the collisionRectY coordinate.
    const droppableContainerY = isDraggingUp ? rect.top : rect.bottom;
    const distance = Math.abs(collisionRectY - droppableContainerY);
    collisions.push({ id, data: { droppableContainer, value: distance } });
  }

  return collisions.sort((a, b) => a.data.value - b.data.value);
};

/**
 * Identifies the last row in the given array where drag-and-drop is disabled, and where all preceding rows are also disabled.
 *
 * @param rows - the table rows
 * @returns the index of the last found row with disabled drag and drop
 */
function findLastDisabledRow(rows: { rowIndex: number; row: Row<unknown> }[]) {
  let lastDisabledRowIndex: number | undefined = undefined;
  for (const { rowIndex, row } of rows) {
    if (row.getCanDrag()) {
      break;
    }
    lastDisabledRowIndex = rowIndex;
  }
  return lastDisabledRowIndex;
}

/**
 * Custom collision detection function that filters droppable containers based the sub-row depth of the dragged row,
 * and then applies the custom closestCenterWithinY algorithm on the filtered targets.
 * Only rows on the same sub-row depth level can be ordered.
 *
 * @param active - The actively dragged item.
 * @param droppableContainers - The list of all droppable containers.
 * @internal
 */
export const rowOrderCollisionDetection: CollisionDetection = ({
  active,
  droppableContainers,
  ...rest
}) => {
  if (active.data.current === undefined) {
    return [];
  }

  const activeRow = active.data.current?.row as Row<unknown>;
  const activeRowIndex = active.data.current?.rowIndex;
  const activeRowSubRowDepth = activeRow?.getSubRowDepth();

  const lastExpandedSubRowsOnLevel = new Set<string>();

  const validTargets = droppableContainers.filter((target) => {
    const targetRow = target.data.current?.row as Row<unknown>;
    const targetRowIndex = target.data.current?.rowIndex;
    const targetRowSubRowDepth = targetRow?.getSubRowDepth();
    const targetRowDisabled = !targetRow?.getCanDrag();
    const targetRowLockedDisabled = targetRow?.getDisabledRowDragAndDropLock;

    const isSameDepthAndParent =
      targetRowSubRowDepth === activeRowSubRowDepth &&
      activeRow.parentId === targetRow.parentId;

    // currently active row is a valid target
    if (activeRowIndex === targetRowIndex) {
      return true;
    }

    if (targetRowDisabled && targetRowLockedDisabled !== false) {
      const rowsByRowIndex = droppableContainers
        .filter(
          (container) => container.data.current?.row.parentId === undefined,
        )
        .map((container) => {
          return {
            rowIndex: container.data.current?.rowIndex,
            row: container.data.current?.row,
          };
        })
        .sort((data1, data2) => data1?.rowIndex - data2?.rowIndex);

      const lastDisabledRowIndexFromStart =
        targetRowLockedDisabled !== 'end'
          ? findLastDisabledRow(rowsByRowIndex)
          : undefined;
      const firstDisabledRowIndexFromEnd =
        targetRowLockedDisabled !== 'start'
          ? findLastDisabledRow(rowsByRowIndex.reverse())
          : undefined;

      // if the disabled row is already (grouped) at the start or end,
      // the row is not a valid target
      if (
        targetRow.parentId === undefined &&
        ((lastDisabledRowIndexFromStart !== undefined &&
          targetRowIndex <= lastDisabledRowIndexFromStart) ||
          (firstDisabledRowIndexFromEnd !== undefined &&
            targetRowIndex >= firstDisabledRowIndexFromEnd))
      ) {
        return false;
      }
    }

    if (isSameDepthAndParent) {
      const hasOpenSubRows =
        targetRow.getCanExpand() && targetRow.getIsExpanded();

      // when active row is moved down, and the target row has expanded sub-rows the row
      // should only be moved when it's moved past the sub-rows
      // therefore, instead of the parent row, the last expanded sub-row is the valid target
      if (activeRowIndex < targetRowIndex && hasOpenSubRows) {
        // get id of last open subrow and save it to be able to make it the actual target
        const lastExpandedRow = targetRow._getLastExpandedSubRow();
        if (lastExpandedRow) {
          lastExpandedSubRowsOnLevel.add(lastExpandedRow.id);
        }
        return false;
      }

      return true;
    }

    if (targetRowSubRowDepth > activeRowSubRowDepth) {
      // if the target is the last expanded sub-row of a target row
      return lastExpandedSubRowsOnLevel.has(targetRow.id);
    }

    return false;
  });

  // Apply closestTargetWithinY on filtered targets
  return closestTargetWithinY({
    active,
    droppableContainers: validTargets,
    ...rest,
  });
};
// #endregion CollisionDetection

// #region Modifiers
/**
 * Restricts the vertical movement of an element within the specified bounds.
 *
 * @param transform - Transform values of the currently dragged element
 * @param rect - The bounding rectangle of the element being transformed.
 * @param verticalBounds - The bounds within which the element should be restricted.
 * @returns The updated transform object with restricted y value.
 * @internal
 */
export function restrictToVerticalBounds(
  transform: DragAndDropTransform,
  rect: ClientRect,
  verticalBounds: {
    top: number;
    bottom: number;
  },
): DragAndDropTransform {
  const value = {
    ...transform,
  };

  if (rect.top + transform.y <= verticalBounds.top) {
    value.y = verticalBounds.top - rect.top;
  } else if (rect.bottom + transform.y >= verticalBounds.bottom) {
    value.y = verticalBounds.bottom - rect.bottom;
  }

  return value;
}

/**
 * Creates a dnd-kit modifier to restrict the dragging to the table row area.
 * @param tableHeaderHeight - This height is subtracted from the parent element height.
 * @param containedVariant - Whether the table is using the contained visual variant.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns dnd-kit modifier
 * @internal
 */
export function createRestrictToTableRowAreaModifier(
  tableHeaderHeight: number,
  containedVariant: boolean,
  virtualizationContainer?: HTMLDivElement,
): Modifier {
  return ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect || virtualizationContainer === undefined) {
      return transform;
    }

    // Restrict dragging to the vertical axis
    transform.x = 0;

    const parentRect = virtualizationContainer.getBoundingClientRect();
    const BORDER_WIDTH = 2;

    // Restrict dragging to the table container minus borders
    const verticalBounds = {
      top: containedVariant
        ? parentRect?.top + tableHeaderHeight + BORDER_WIDTH
        : parentRect?.top + tableHeaderHeight,
      bottom: containedVariant
        ? parentRect?.bottom - BORDER_WIDTH
        : parentRect?.bottom,
    };

    return restrictToVerticalBounds(
      transform,
      draggingNodeRect,
      verticalBounds,
    );
  };
}
// #endregion Modifiers
